import React, { useEffect, useMemo, useRef, useState } from "react";
import { PropsWithChildren } from "react";
import { createPortal } from "react-dom";
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalDesc,
  ModalActions,
  ModalBtn,
  Field,
  Label,
  InputWrap,
  LeftIcon,
  Input,
  Hint,
} from "./styles";
import { anywareAPI } from "api";

const ModalPortal = ({ children }: PropsWithChildren<{}>) => {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
};

const InstanceModal = ({ isOpen, onClose, selectUser, mode }) => {
  const [instanceName, setInstanceName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasSelection = (selectUser?.length ?? 0) > 0;
  const nameValid = /^[a-zA-Z0-9._-]{3,30}$/.test(instanceName);

  const canSubmit = useMemo(
    () => nameValid && hasSelection && !submitting,
    [nameValid, hasSelection, submitting]
  );

  // 열릴 때 포커스 & 닫힐 때 정리
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setInstanceName("");
      setSubmitting(false);
    }
  }, [isOpen]);

  // 키 핸들링 (Esc/Enter)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter" && canSubmit) handleSubmit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, canSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      if (mode === "add") {
        for (const u of selectUser) {
          await anywareAPI.putEntitlement(u.name, instanceName);
        }
      } else {
        for (const u of selectUser) {
          await anywareAPI.deleteEntitlement(u.name, instanceName);
        }
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const isDelete = mode === "delete";
  const titleText = isDelete ? "인스턴스 삭제" : "인스턴스 추가";
  const descText = `선택된 유저 ${
    selectUser?.length ?? 0
  }명에 대해 인스턴스를 ${isDelete ? "삭제" : "생성"}합니다.`;

  return (
    <ModalPortal>
      <ModalOverlay
        role="dialog"
        aria-modal="true"
        aria-labelledby="instance-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle id="instance-title">{titleText}</ModalTitle>
          <ModalDesc>{descText}</ModalDesc>

          {/* 공통: 인스턴스 이름 */}
          <Field>
            <Label htmlFor="instanceName">Instance name</Label>
            <InputWrap data-valid={nameValid}>
              <LeftIcon viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M3 5h18v6H3zM3 13h18v6H3zM8 8h.01M8 16h.01M17 8h.01M17 16h.01"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </LeftIcon>
              <Input
                id="instanceName"
                ref={inputRef}
                placeholder="예: oasys-0101"
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                aria-invalid={!nameValid}
                required
                aria-required="true"
              />
            </InputWrap>
            <Hint $invalid={!nameValid}>영문/숫자/._- 조합, 3~30자.</Hint>
          </Field>

          <ModalActions>
            <ModalBtn onClick={onClose} disabled={submitting}>
              취소
            </ModalBtn>
            <ModalBtn $danger onClick={handleSubmit} disabled={!canSubmit}>
              {submitting
                ? isDelete
                  ? "삭제 중..."
                  : "생성 중..."
                : isDelete
                ? "삭제"
                : "생성"}
            </ModalBtn>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default InstanceModal;
